/**
 * Quiz funnel — Cogistick pre-lander.
 *
 * Screens: who -> console -> search -> result | miss.
 * Markup for every screen is rendered server-side by sections/quiz-funnel.liquid, so
 * this file only drives state. Nothing here writes layout, which keeps CLS at zero.
 *
 * The game index is the same file the PDP "Is your game on it?" widget reads
 * (Content > Files). One truth, two widgets. It is ~152 KB gzipped, so it is fetched
 * lazily on the console tap — never on first paint.
 */
(function () {
  var root = document.querySelector('[data-quiz-funnel]');
  if (!root) return;

  var cfg = {};
  try {
    cfg = JSON.parse(root.querySelector('[data-quiz-config]').textContent);
  } catch (e) {
    return;
  }

  var HEARTS_FILLED = { who: 0, console: 1, search: 2, result: 3, miss: 3 };

  var state = { mode: 'self', console: null, game: null };
  var index = null;
  var indexLoading = false;

  function el(sel, ctx) { return (ctx || root).querySelector(sel); }
  function all(sel, ctx) { return [].slice.call((ctx || root).querySelectorAll(sel)); }

  /* ------------------------------------------------------------------ analytics */

  function track(event, payload) {
    payload = payload || {};
    try {
      if (typeof fbq === 'function') {
        if (event === 'Lead') fbq('track', 'Lead', payload);
        else fbq('trackCustom', event, payload);
      }
    } catch (e) { /* pixel blocked — never break the funnel */ }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: event }, payload));
    } catch (e) { /* noop */ }
  }

  /* ------------------------------------------------------- normalise and match */

  var ROMAN = { i: '1', ii: '2', iii: '3', iv: '4', v: '5', vi: '6', vii: '7', viii: '8', ix: '9', x: '10' };

  function norm(s) {
    return String(s)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  /** Second key so roman-numeral and digit forms cross-match both ways. */
  function numKey(n) {
    if (!n) return n;
    return n.split(' ').map(function (t) { return ROMAN[t] || t; }).join(' ');
  }

  var ALIAS = {
    'pacman': 'pac man',
    'pokemon': 'pokemon red',
    'goldeneye 007': 'goldeneye',
    '007 goldeneye': 'goldeneye',
    'sonic': 'sonic the hedgehog',
    'tony hawk': 'tony hawk s pro skater',
    'mario': 'super mario bros',
    'zelda': 'the legend of zelda'
  };

  function loadIndex() {
    if (index || indexLoading) return Promise.resolve(index);
    indexLoading = true;
    return fetch(cfg.indexUrl)
      .then(function (r) { return r.json(); })
      .then(function (rows) {
        var mask = cfg.cardMask || 2;
        index = [];
        for (var i = 0; i < rows.length; i++) {
          // Titles that live only on a card we no longer ship must never return a hit.
          if ((rows[i][1] & mask) === 0) continue;
          var a = norm(rows[i][0]);
          index.push({ n: rows[i][0], a: a, b: numKey(a) });
        }
        return index;
      })
      .catch(function () { index = null; return null; })
      .finally(function () { indexLoading = false; });
  }

  function best(list) {
    if (!list.length) return null;
    // Shortest normalised title first: "Pac-Man" beats "Hyper Pacman".
    list.sort(function (x, y) {
      if (x.a.length !== y.a.length) return x.a.length - y.a.length;
      return x.n.toLowerCase() < y.n.toLowerCase() ? -1 : 1;
    });
    return list[0];
  }

  function resolve(query) {
    if (!index) return null;
    var a = norm(query);
    if (ALIAS[a]) a = ALIAS[a];
    if (!a) return null;
    var b = numKey(a), i, hit, out = [];

    for (i = 0; i < index.length; i++) { hit = index[i]; if (hit.a === a || hit.b === b) out.push(hit); }
    if (out.length) return best(out);

    for (i = 0; i < index.length; i++) { hit = index[i]; if (hit.a.indexOf(a) === 0 || hit.b.indexOf(b) === 0) out.push(hit); }
    if (out.length) return best(out);

    if (a.length >= 3) {
      for (i = 0; i < index.length; i++) { hit = index[i]; if (hit.a.indexOf(a) !== -1 || hit.b.indexOf(b) !== -1) out.push(hit); }
      if (out.length) return best(out);
    }
    return null;
  }

  /* ---------------------------------------------------------------- navigation */

  function show(name, push) {
    all('[data-quiz-screen]').forEach(function (s) {
      s.classList.toggle('is-active', s.dataset.quizScreen === name);
    });

    var filled = HEARTS_FILLED[name] || 0;
    all('[data-quiz-heart]').forEach(function (h, i) { h.classList.toggle('is-full', i < filled); });

    if (push !== false) {
      try { history.pushState({ quiz: name }, '', location.href); } catch (e) { /* noop */ }
    }
  }

  window.addEventListener('popstate', function (e) {
    var name = (e.state && e.state.quiz) || 'who';
    // Step back inside the quiz rather than leaving the page.
    show(name, false);
  });

  /* ------------------------------------------------------------------ mode copy */

  function applyMode() {
    var gift = state.mode === 'gift';
    all('[data-copy-self]').forEach(function (n) {
      n.textContent = gift ? n.dataset.copyGift : n.dataset.copySelf;
    });
  }

  /* -------------------------------------------------------------------- result */

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function renderResult() {
    var gift = state.mode === 'gift';
    var game = state.game;
    var con = state.console;

    // The live game index carries no console/system column, so the game's console
    // group cannot be derived and the "{count} other {console} games" variant stays
    // off. When a system-tagged manifest lands, set group on the game and count on
    // the console and this switches to the count line with no other change.
    var line;
    if (con.count && game.group && game.group === con.id) {
      line = '<b>' + esc(game.n) + '</b> is included — along with ' + con.count +
             ' other ' + (con.alt || con.label + ' games') + '.';
    } else {
      line = '<b>' + esc(game.n) + '</b> is included. And so is everything ' +
             (gift ? 'they' : 'you') + ' played on the ' + esc(con.label) + '.';
    }
    el('[data-quiz-confirm]').innerHTML = line;

    el('[data-quiz-era]').textContent = gift
      ? 'Give them back ' + con.year + ' — the games, the feeling, all of it.'
      : 'Go back to ' + con.year + ' — the couch, the games, one more level before bed.';

    var under = gift ? cfg.ctaSubtextGift : cfg.ctaSubtextSelf;
    el('[data-quiz-under]').textContent = String(under).replace('{price}', cfg.price);

    el('[data-quiz-cta]').setAttribute('href', ctaHref());
  }

  /** Product URL + attribution + whatever the ad handed us on landing. */
  function ctaHref() {
    var url = cfg.productUrl.split('#')[0].split('?')[0];
    var params = ['ref=quiz', 'console=' + encodeURIComponent(state.console.id), 'mode=' + encodeURIComponent(state.mode)];
    Object.keys(passthrough).forEach(function (k) {
      params.push(encodeURIComponent(k) + '=' + encodeURIComponent(passthrough[k]));
    });
    return url + '?' + params.join('&');
  }

  // Captured on land: searched titles are never appended here, only to the dataLayer.
  var passthrough = {};
  (function capture() {
    try {
      new URLSearchParams(location.search).forEach(function (v, k) {
        var lower = k.toLowerCase();
        if (/^utm_/.test(lower) || lower === 'fbclid' || lower === 'gclid') passthrough[k] = v;
      });
    } catch (e) { /* noop */ }
  })();

  /* --------------------------------------------------------------------- wiring */

  function press(button, done) {
    button.classList.add('is-picked');
    setTimeout(function () { button.classList.remove('is-picked'); done(); }, 240);
  }

  all('[data-quiz-mode]').forEach(function (b) {
    b.addEventListener('click', function () {
      press(b, function () {
        state.mode = b.dataset.quizMode;
        applyMode();
        track('QuizStep', { step: 1, answer: state.mode });
        show('console');
      });
    });
  });

  all('[data-quiz-console]').forEach(function (b) {
    b.addEventListener('click', function () {
      // Warm the index now so it is ready by the time the search screen is reached.
      loadIndex();
      press(b, function () {
        state.console = JSON.parse(b.dataset.quizConsole);
        showChipsFor(state.console.id);
        track('QuizStep', { step: 2, answer: state.console.id });
        show('search');
      });
    });
  });

  /** The classics row is per console: N64 offers N64 titles, Game Boy offers Game Boy titles. */
  function showChipsFor(id) {
    all('[data-quiz-chips-for]').forEach(function (row) {
      row.classList.toggle('is-active', row.dataset.quizChipsFor === id);
    });
  }

  var input = el('[data-quiz-input]');

  input.addEventListener('focus', function () {
    // Keeps the field and the chips above the mobile keyboard.
    setTimeout(function () {
      input.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 250);
  });

  input.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') { e.preventDefault(); submit(input.value); }
  });

  all('[data-quiz-chip]').forEach(function (b) {
    b.addEventListener('click', function () {
      input.value = b.dataset.quizChip;
      submit(b.dataset.quizChip);
    });
  });

  function submit(raw) {
    var value = String(raw || '').trim();
    if (value.length < 2) return;

    loadIndex().then(function () {
      var hit = resolve(value);
      if (hit) {
        state.game = hit;
        track('QuizSearchHit', { title: hit.n });
        renderResult();
        show('result');
        track('Lead', { content_name: 'quiz_complete', console: state.console.id, mode: state.mode });
      } else {
        track('QuizSearchMiss', { query: value });
        show('miss');
      }
    });
  }

  el('[data-quiz-retry]').addEventListener('click', function () {
    input.value = '';
    show('search');
    setTimeout(function () { input.focus(); }, 80);
  });

  el('[data-quiz-cta]').addEventListener('click', function () {
    track('QuizCTA', { console: state.console.id, mode: state.mode });
  });

  /* ---------------------------------------------------------------------- start */

  applyMode();
  track('QuizStart', {});
  // Deep entry always starts at screen 1.
  show('who', false);
  try { history.replaceState({ quiz: 'who' }, '', location.href); } catch (e) { /* noop */ }
})();
